import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAdmin } from "@/context/AdminContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { login } = useAdmin();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.trim().length < 4) {
      toast.error("La password deve avere almeno 4 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reimpostata con successo");
      setDone(true);
      try {
        await login(password);
      } catch (_) {}
      setTimeout(() => navigate("/gestione"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link non valido o scaduto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background noise-bg p-5" data-testid="reset-password-page">
      <div className="w-full max-w-md border border-border bg-card p-8 sm:p-10 relative z-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 bg-rugby-yellow flex items-center justify-center font-head font-bold text-black text-lg -skew-x-6">
            RCV
          </div>
          <div>
            <div className="font-head font-bold uppercase tracking-tight leading-none">Valpolicella</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-rugby-yellow mt-1">Rugby U14 · Presenze</div>
          </div>
        </div>

        {!token ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground text-sm">Link non valido: token mancante.</p>
          </div>
        ) : done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="h-14 w-14 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-6 w-6 text-emerald-400" />
            </div>
            <h3 className="font-head uppercase tracking-tight text-2xl font-bold mb-1">Fatto!</h3>
            <p className="text-muted-foreground text-sm">Password aggiornata. Reindirizzamento in corso...</p>
          </div>
        ) : (
          <>
            <div className="h-12 w-12 bg-rugby-yellow/10 border border-rugby-yellow/30 flex items-center justify-center mb-4">
              <KeyRound className="h-5 w-5 text-rugby-yellow" />
            </div>
            <h3 className="font-head uppercase tracking-tight text-2xl font-bold mb-2">Reimposta Password</h3>
            <p className="text-muted-foreground text-sm mb-6">Scegli una nuova password per l'area di gestione.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Nuova password
                </Label>
                <div className="relative">
                  <Input
                    type={show ? "text" : "password"}
                    data-testid="reset-new-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-black/50 border-border focus-visible:ring-rugby-yellow pr-10"
                    autoFocus
                  />
                  <button
                    type="button"
                    data-testid="reset-toggle-password"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-rugby-yellow"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Conferma nuova password
                </Label>
                <Input
                  type={show ? "text" : "password"}
                  data-testid="reset-confirm-password-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
                />
              </div>
              <Button
                type="submit"
                data-testid="reset-submit"
                disabled={loading}
                className="w-full bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
              >
                {loading ? "Aggiornamento..." : "Reimposta Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
